/**
 * The App component is the component that is rendered around all views, and
 * contains common things like navigation, footer, etc.
 */
import 'react-dates/initialize'
import { type Home, type User, UserRole } from '@lmc/ts-common'
import LazyImportErrorBoundary from 'components/LazyImportErrorBoundary/LazyImportErrorBoundary'
import MixpanelEventsForRoutes from 'components/MixpanelEventsForRoutes'
import { PrimaryNavigation } from 'components/navigation'
import { useIntercomReady } from 'hooks/useIntercomReady'
import { get } from 'lodash'
import type React from 'react'
import { useEffect, useState } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { connect } from 'react-redux'
import { Outlet, useLocation } from 'react-router-dom'
import useQueryInvalidation from 'screens/Rostering/hooks/useQueryInvalidation'
import { INTERCOM_PATHS } from './common/constants'
import { hideIntercom, showIntercom } from './common/services/intercom'
import { ErrorBoundary, Spinner } from './components/generic'
import MountedModals from './components/modals/MountedModals'
import {
  initialiseAppSession,
  initialisePermissions
} from './operations/Global'
import { history } from './store'

interface RootProps {
  router: typeof history
  initialiseSession: (initResponse: any) => void
  initialisePermissions: () => void
  user: User
  home: Home
  initResponse: any
}

export const Root = ({
  user,
  initialiseSession,
  initialisePermissions,
  router = history,
  home,
  initResponse
}: RootProps) => {
  const [sectionKey, setSectionKey] = useState('dashboard')

  const location = useLocation()

  const userRole = user?.role

  useQueryInvalidation()

  useEffect(() => {
    initialiseSession(initResponse)
    initialisePermissions()
    if (router.location.pathname.match(/^\/admin/)) {
      router.push(router.location.pathname.replace(/^\/admin/, '/'))
    }
  }, [])

  const isOnBoardingScreen = router.location.pathname.startsWith('/onboarding')

  const mfaScreen = router.location.pathname.startsWith('/mfa')

  const isIntercomReady = useIntercomReady()

  useEffect(() => {
    // Only show Intercom icon on permitted screens
    if (location.pathname) {
      const isShowingIntercom = INTERCOM_PATHS.includes(location.pathname)
      if (isShowingIntercom) showIntercom()
      else hideIntercom()
    }
  }, [location.pathname, isIntercomReady])

  useEffect(() => {
    history.listen(() => {
      const {
        location: { pathname }
      } = history
      let key
      if (pathname.match('/reports')) {
        key = 'reports'
      } else if (pathname.match('/todos')) {
        key = 'todos'
      } else if (pathname.match('/homes')) {
        key = 'homes'
      } else if (pathname.match('/residents')) {
        key = 'residents'
      } else if (pathname.match('/organisation')) {
        key = 'organisation'
      } else if (pathname.match('/settings')) {
        key = 'settings'
      } else if (pathname.match('/group-reports')) {
        key = 'group-reports'
      } else if (pathname.match('/logs')) {
        key = 'logs'
      } else {
        key = 'dashboard'
      }
      setSectionKey(key)
    })
  }, [history])

  const isCareHomeAdmin = userRole === UserRole.careHomeAdmin

  const hasHomeSetup =
    home?.preferencesCustomised &&
    (user as unknown as { hasPin: boolean })?.hasPin

  useEffect(() => {
    if (isCareHomeAdmin) {
      if (hasHomeSetup === false) {
        router.push('/onboarding')
        return
      }
      if (hasHomeSetup === true && location.pathname === '/onboarding') {
        router.push('/')
      }
    }
  }, [hasHomeSetup, router.action, location.pathname, userRole])

  if (!userRole || !location) {
    return (
      <div style={styles.spinnerContainer as React.CSSProperties}>
        <img style={styles.spinner} src="/images/lmc_logo_large.png" alt="" />
        <Spinner />
      </div>
    )
  }

  return (
    <div style={styles.wrapper}>
      <MixpanelEventsForRoutes />
      <LazyImportErrorBoundary>
        {!isOnBoardingScreen && !mfaScreen && (
          <PrimaryNavigation currentSectionKey={sectionKey} />
        )}
        <main style={styles.body}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
        <MountedModals router={router} />
      </LazyImportErrorBoundary>
    </div>
  )
}

const styles = {
  body: {
    flexGrow: 1
  },
  wrapper: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    minHeight: '100vh',
    minWidth: isBrowser || isTablet ? 800 : 0
  },
  spinner: {
    width: '20vw',
    marginTop: '40vh'
  },
  spinnerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  }
}

const mapStateToProps = (state: unknown) => ({
  user: get(state, 'global.user'),
  home: get(state, 'global.home')
})

const mapDispatchToProps = (dispatch: any) => ({
  initialiseSession: (initResponse: any) =>
    dispatch(initialiseAppSession(initResponse)),
  initialisePermissions: () => dispatch(initialisePermissions())
})

export default connect<any, any, any>(
  mapStateToProps,
  mapDispatchToProps
)(Root as any)
